import { createContext, useContext, useReducer } from "react";

const ACCOUNT_FULL_LIST = [
    { name: 'Labour', prop: 'labour' },
    { name: 'Linens', prop: 'linens' },
    { name: 'Extra Linens', prop: 'extra_linens' },
    { name: 'Consumable', prop: 'consumable' },
    { name: 'Cleaner Extra', prop: 'extra' },
    { name: 'Call Out', prop: 'call_out' },
    { name: 'Key Delivery', prop: 'key_delivery' },
    { name: 'Key Return', prop: 'key_return' },
    { name: 'Carpet Cleaning', prop: 'carpet_cleaning' },
    { name: 'Agent Labour Surcharge', prop: 'labour_surcharge' },
    { name: 'Credit', prop: 'credit' },
    { name: 'Parking', prop: 'parking' },
    { name: 'Long Distance Surcharge', prop: 'long_distance' },
];

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

const initialState = {
    myobProfiles: [],
    propertyPrice: [],
    jobPrice: [],
    workOrder: [],
    businessesAndAccounts: {},
    accountsSelection: {},
    accountFullList: ACCOUNT_FULL_LIST,
    searchAddress: '',
    searchAgent: '',
    searchJobId: '',
    filterStartDate: new Date(Date.now() - THIRTY_DAYS_MS),
    filterEndDate: new Date(Date.now() + THREE_DAYS_MS),
    filterRegion: null,
    allRegions: [],
    invoiceGroupType: 'PER_JOB',
    isInvoiceInProgress: false,
    loading: false,
    notification: null,
};

function buildAccountsSelection(businessesAndAccounts) {
    let nextSelection = {};
    Object.keys(businessesAndAccounts).forEach((key) => {
        nextSelection[key] = {};
        const businesses = businessesAndAccounts[key];
        if (Array.isArray(businesses)) {
            businesses.forEach((business) => {
                nextSelection[key][business.uid] = {};
                if (business.accounts_mapping) {
                    Object.keys(business.accounts_mapping).forEach((accountKey) => {
                        nextSelection[key][business.uid][accountKey] = business.accounts_mapping[accountKey].uid;
                    });
                }
            });
        }
    });
    return nextSelection;
}

function accountantReducer(state, action) {
    switch (action.type) {
        case 'SET_JOB_PRICING':
            return { ...state, jobPrice: action.payload };
        case 'SET_PROPERTY_PRICING':
            return { ...state, propertyPrice: action.payload };
        case 'SET_WORK_ORDERS':
            return { ...state, workOrder: action.payload };
        case 'SET_MYOB_PROFILES':
            return { ...state, myobProfiles: action.payload };
        case 'SET_BUSINESSES_ACCOUNTS':
            return {
                ...state,
                businessesAndAccounts: action.payload,
                accountsSelection: buildAccountsSelection(action.payload),
            };
        case 'DELETE_PROFILE': {
            const nextProfiles = state.myobProfiles.filter(p => p.uid !== action.payload);
            return { ...state, myobProfiles: nextProfiles };
        }
        case 'UPDATE_JOB_CELL': {
            const newJobPrice = state.jobPrice.map(job => {
                if (job.cos_job_id === action.payload.cos_job_id) {
                    return { ...job, [action.payload.cell]: action.payload.value };
                }
                return job;
            });
            return { ...state, jobPrice: newJobPrice };
        }
        case 'UPDATE_PROPERTY_CELL': {
            const newPropertyPrice = state.propertyPrice.map(prop => {
                if (prop.property_id === action.payload.propertyId) {
                    return { ...prop, [action.payload.cell]: action.payload.value };
                }
                return prop;
            });
            return { ...state, propertyPrice: newPropertyPrice };
        }
        case 'SET_SEARCH_ADDRESS':
            return { ...state, searchAddress: action.payload };
        case 'SET_SEARCH_AGENT':
            return { ...state, searchAgent: action.payload };
        case 'SET_SEARCH_JOB_ID':
            return { ...state, searchJobId: action.payload };
        case 'SET_REGION':
            return { ...state, filterRegion: action.payload };
        case 'SET_ALL_REGIONS':
            return { ...state, allRegions: action.payload };
        case 'SET_START_DATE':
            return { ...state, filterStartDate: action.payload };
        case 'SET_END_DATE':
            return { ...state, filterEndDate: action.payload };
        case 'SET_GROUP_TYPE':
            return { ...state, invoiceGroupType: action.payload };
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_NOTIFICATION':
            return { ...state, notification: action.payload };
        case 'SET_INVOICE_IN_PROGRESS':
            return { ...state, isInvoiceInProgress: action.payload };
        default:
            return state;
    }
}

const AccountantContext = createContext(null);

export function AccountantProvider({ children }) {
    const [state, dispatch] = useReducer(accountantReducer, initialState);
    return (
        <AccountantContext.Provider value={[state, dispatch]}>
            {children}
        </AccountantContext.Provider>
    );
}

export function useAccountant() {
    const context = useContext(AccountantContext);
    if (!context) {
        throw new Error('useAccountant must be used within AccountantProvider');
    }
    return context;
}
